import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import GoldLayerRenderer from "./GoldLayerRenderer";
import H2HMatchupChip from "./H2HMatchupChip";
import HardeningChip from "./HardeningChip";
import {
  Home, Plane, MapPin, Trophy, Flag, CloudSun, LineChart, Users,
  Brain, Clock, Shield, Zap, Target, Activity, Layers, ChevronDown,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const DAYS = [
  { key: "yesterday", label: "Yesterday" },
  { key: "today", label: "Today" },
  { key: "tomorrow", label: "Tomorrow" },
];

// Formats an ISO date to "HH:mm"
const fmtTime = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
  } catch { return ""; }
};

// ─── Fixture picker ───────────────────────────────────────────
const FixturePicker = ({ token, onSelect, selectedId }) => {
  const [day, setDay] = useState("today");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    setLoading(true);
    axios.get(`${API}/api/gold/fixtures/feed`, {
      params: { day, limit: 200 },
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => { if (!cancelled) setRows(r.data.fixtures || []); })
      .catch(() => { if (!cancelled) setRows([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [token, day]);

  const filtered = useMemo(() => {
    if (!search.trim()) return rows;
    const q = search.toLowerCase();
    return rows.filter((r) =>
      (r.home || "").toLowerCase().includes(q) ||
      (r.away || "").toLowerCase().includes(q) ||
      (r.league || "").toLowerCase().includes(q) ||
      (r.country || "").toLowerCase().includes(q)
    );
  }, [rows, search]);

  return (
    <Card className="bg-black/70 border-amber-500/20 mb-4">
      <CardContent className="p-3">
        <div className="flex flex-col md:flex-row gap-2 md:items-center mb-2">
          <div className="flex gap-1">
            {DAYS.map((d) => (
              <button
                key={d.key}
                onClick={() => setDay(d.key)}
                className={`px-3 py-1 text-[11px] rounded uppercase tracking-widest font-semibold border transition-colors ${
                  day === d.key
                    ? "bg-amber-500/30 text-amber-100 border-amber-400"
                    : "bg-black/60 text-amber-500/70 border-amber-500/25 hover:bg-amber-500/10"
                }`}
                data-testid={`dossier-day-${d.key}`}
              >
                {d.label}
              </button>
            ))}
          </div>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Filter by team / league / country…"
            data-testid="dossier-fixture-search"
            className="flex-1 bg-black/60 border border-amber-500/25 rounded px-3 py-1 text-[12px] text-amber-100 placeholder:text-amber-500/40 focus:outline-none focus:border-amber-400"
          />
          <div className="text-[11px] text-amber-500/60 whitespace-nowrap">
            {loading ? "Loading…" : `${filtered.length} matches`}
          </div>
        </div>
        <div className="max-h-64 overflow-y-auto rounded border border-amber-500/10 divide-y divide-amber-500/10" data-testid="dossier-fixture-list">
          {filtered.length === 0 && !loading && (
            <div className="p-3 text-[11px] text-amber-500/60 italic text-center">No fixtures found.</div>
          )}
          {filtered.map((f) => {
            const active = f.fixture_id === selectedId;
            const isSettled = ["FT", "AET", "PEN"].includes(f.status);
            return (
              <button
                key={f.fixture_id}
                onClick={() => onSelect(f)}
                data-testid={`dossier-fixture-${f.fixture_id}`}
                className={`w-full flex items-center gap-3 px-3 py-2 text-left transition-colors ${
                  active ? "bg-amber-500/15" : "hover:bg-amber-500/5"
                }`}
              >
                <div className="text-[10px] text-amber-500/60 tabular-nums w-10">
                  {isSettled ? "FT" : fmtTime(f.date)}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[12px] text-amber-100 font-semibold truncate">
                    {f.home} <span className="text-amber-500/60">vs</span> {f.away}
                    {isSettled && f.goals_home != null && (
                      <span className="ml-2 text-amber-300 font-mono">({f.goals_home}-{f.goals_away})</span>
                    )}
                  </div>
                  <div className="text-[10px] text-amber-500/60 truncate">
                    {f.country && <>{f.country} · </>}{f.league}
                  </div>
                </div>
                {active && <div className="text-[10px] text-amber-400 uppercase tracking-widest">Selected</div>}
              </button>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};

// ─── Small layer card ─────────────────────────────────────────
const LayerCard = ({ icon: Icon, title, id, data, subtitle }) => (
  <Card className="bg-black/70 border-amber-500/20" data-testid={`dossier-layer-${id}`}>
    <CardContent className="p-3">
      <div className="flex items-center gap-2 mb-3">
        {Icon && <Icon className="w-4 h-4 text-amber-400" />}
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-widest text-amber-500/60">Layer {id}</div>
          <div className="text-[13px] font-bold text-amber-100 truncate">{title}</div>
          {subtitle && <div className="text-[10px] text-amber-500/60 truncate">{subtitle}</div>}
        </div>
      </div>
      {!data ? (
        <div className="text-[11px] text-amber-500/60 italic">Not loaded</div>
      ) : data.error ? (
        <div className="text-[11px] text-red-400">Error: {data.error}</div>
      ) : (
        <GoldLayerRenderer layerId={id} data={data} />
      )}
    </CardContent>
  </Card>
);

// ─── Team column ──────────────────────────────────────────────
const TeamColumn = ({ side, teamName, layers, teamIcon: TIcon }) => (
  <div className="space-y-3">
    <div className="flex items-center gap-2 pb-1 border-b border-amber-500/25">
      <TIcon className="w-4 h-4 text-amber-400" />
      <div className="text-[10px] uppercase tracking-widest text-amber-500/70">{side}</div>
      <div className="text-sm font-bold text-amber-100 truncate">{teamName}</div>
    </div>
    <LayerCard icon={Trophy} title="Team DNA" id={3} data={layers[`3_team_dna_${side.toLowerCase()}`]} />
    <LayerCard icon={Shield} title="Defensive Shots" id={6} data={layers[`6_defensive_shots_${side.toLowerCase()}`]} />
    <LayerCard icon={Clock} title="Time Intelligence" id={7} data={layers[`7_time_intel_${side.toLowerCase()}`]} />
    <LayerCard icon={Activity} title="Tactical" id={8} data={layers[`8_tactical_${side.toLowerCase()}`]} />
    <LayerCard icon={Brain} title="Psychological" id={13} data={layers[`13_psychological_${side.toLowerCase()}`]} />
    <LayerCard icon={LineChart} title="Team Patterns" id={15} data={layers[`15_team_patterns_${side.toLowerCase()}`]} />
    <LayerCard icon={Users} title="Player Synergy" id={16} data={layers[`16_synergy_${side.toLowerCase()}`]} />
    <LayerCard icon={Clock} title="Schedule" id={12} data={layers[`12_schedule_${side.toLowerCase()}`]} />
  </div>
);

// ─── Main dossier view ────────────────────────────────────────
const DossierView = ({ token, fixture }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!fixture || !token) return;
    setLoading(true);
    setErr(null);
    setData(null);
    axios.get(`${API}/api/gold/dossier/${fixture.fixture_id}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => setData(r.data))
      .catch((e) => setErr(e?.response?.data?.detail || e.message))
      .finally(() => setLoading(false));
  }, [fixture, token]);

  if (!fixture) {
    return (
      <Card className="bg-black/70 border-amber-500/20">
        <CardContent className="p-8 text-center">
          <Layers className="w-10 h-10 text-amber-500/40 mx-auto mb-3" />
          <div className="text-sm text-amber-300 font-semibold">Pick a match above</div>
          <div className="text-[11px] text-amber-500/60 mt-1">All 20 layers auto-populate for the fixture you choose.</div>
        </CardContent>
      </Card>
    );
  }

  if (loading) {
    return (
      <Card className="bg-black/70 border-amber-500/20">
        <CardContent className="p-8 text-center">
          <div className="text-sm text-amber-400 animate-pulse">Compiling intelligence briefing…</div>
          <div className="text-[10px] text-amber-500/60 mt-1">Running all 20 layers in parallel</div>
        </CardContent>
      </Card>
    );
  }

  if (err) {
    return (
      <Card className="bg-black/70 border-red-500/40">
        <CardContent className="p-4 text-center">
          <div className="text-sm text-red-300">Failed to load: {err}</div>
        </CardContent>
      </Card>
    );
  }

  if (!data) return null;
  const L = data.layers || {};
  const home = data.teams?.home?.name || "Home";
  const away = data.teams?.away?.name || "Away";
  const kickoff = data.kickoff ? new Date(data.kickoff) : null;

  return (
    <div className="space-y-4">
      {/* Match header */}
      <Card className="bg-gradient-to-r from-amber-900/40 via-black to-amber-900/40 border-amber-500/40">
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row items-center gap-3 md:gap-6">
            <div className="text-right flex-1">
              <div className="text-[10px] uppercase tracking-widest text-amber-500/60">Home</div>
              <div className="text-lg md:text-xl font-bold text-amber-100">{home}</div>
            </div>
            <div className="text-center">
              <div className="text-[10px] uppercase tracking-widest text-amber-500/60">Kickoff</div>
              <div className="text-xl font-mono text-amber-300">
                {kickoff ? kickoff.toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : "—"}
              </div>
              {data.status && ["FT", "AET", "PEN"].includes(data.status) ? (
                <div className="text-2xl font-bold text-amber-200 mt-1">
                  {data.goals?.home ?? "?"} <span className="text-amber-500/50">-</span> {data.goals?.away ?? "?"}
                </div>
              ) : (
                <div className="text-[10px] text-amber-500/60 mt-1">{data.league?.name}</div>
              )}
            </div>
            <div className="text-left flex-1">
              <div className="text-[10px] uppercase tracking-widest text-amber-500/60">Away</div>
              <div className="text-lg md:text-xl font-bold text-amber-100">{away}</div>
            </div>
          </div>
          <div className="flex flex-wrap gap-3 mt-3 pt-3 border-t border-amber-500/20 text-[11px] text-amber-500/70">
            <div className="flex items-center gap-1"><Flag className="w-3 h-3" />{data.league?.name || "—"}</div>
            {data.venue && <div className="flex items-center gap-1"><MapPin className="w-3 h-3" />{data.venue}</div>}
            {data.referee && <div className="flex items-center gap-1"><Flag className="w-3 h-3" />Referee: {data.referee}</div>}
          </div>
        </CardContent>
      </Card>

      {/* Ensemble prediction top-line */}
      {L["18_ensemble"] && !L["18_ensemble"].status && (
        <LayerCard icon={Brain} title="ML Ensemble Decision" id={18} data={L["18_ensemble"]} subtitle="Adaptive calibrated prediction" />
      )}

      {/* Iter 55 UI — H2H Matchup Chip.  Surfaces the SAME payload the
          prediction algorithms consumed (dominance, trend, venue split,
          adjustments applied + audit) so users see the H2H at work. */}
      {L["h2h"] && L["h2h"].status !== "no_data" && L["h2h"].status !== "engine_error" && (
        <H2HMatchupChip h2h={L["h2h"]} homeName={home} awayName={away} />
      )}

      {/* Iter 57/58 UI — Hardening Chip.  Surfaces opponent-adjusted
          form, data-quality score, veto rules fired, and confirmed
          lineup status so users see the model's self-critique. */}
      {L["hardening"] && (
        <HardeningChip
          hardening={L["hardening"].hardening}
          dataQuality={L["hardening"].data_quality}
          veto={L["hardening"].veto}
          lineup={L["hardening"].lineup}
          tactical={L["hardening"].tactical}
        />
      )}

      {/* Match-level context */}
      <div className="grid md:grid-cols-2 gap-3">
        {L["17_market"] && <LayerCard icon={Target} title="Market Intelligence" id={17} data={L["17_market"]} subtitle="Fair odds + value picks" />}
        {L["h2h"] && <LayerCard icon={Zap} title="H2H Deep Dive" id="h2h" data={L["h2h"]} subtitle={`${home} vs ${away}`} />}
        {L["2_league_intel"] && <LayerCard icon={Trophy} title="League Intelligence" id={2} data={L["2_league_intel"]} subtitle={data.league?.name} />}
        {L["14_league_patterns"] && <LayerCard icon={LineChart} title="League Patterns" id={14} data={L["14_league_patterns"]} subtitle="Exploitable market edges" />}
        {L["10_venue"] && <LayerCard icon={MapPin} title="Venue Intelligence" id={10} data={L["10_venue"]} subtitle={data.venue} />}
        {L["11_weather"] && <LayerCard icon={CloudSun} title="Weather" id={11} data={L["11_weather"]} />}
        {L["9_referee"] && <LayerCard icon={Flag} title="Referee" id={9} data={L["9_referee"]} subtitle={data.referee} />}
        {L["1_acquisition"] && <LayerCard icon={Layers} title="Data Acquisition" id={1} data={L["1_acquisition"]} subtitle="Provider health" />}
      </div>

      {/* Home vs Away side-by-side */}
      <div className="grid md:grid-cols-2 gap-3">
        <TeamColumn side="Home" teamName={home} layers={L} teamIcon={Home} />
        <TeamColumn side="Away" teamName={away} layers={L} teamIcon={Plane} />
      </div>
    </div>
  );
};

// ─── Public component ─────────────────────────────────────────
export default function GoldMatchDossier({ token }) {
  const [selected, setSelected] = useState(null);
  return (
    <div>
      <FixturePicker token={token} onSelect={setSelected} selectedId={selected?.fixture_id} />
      <DossierView token={token} fixture={selected} />
    </div>
  );
}
